import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, LayoutGrid, Radio, MessageCircle, ShoppingCart, User } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LangContext";
import { useSiteConfig } from "../context/ConfigContext";

export default function BottomNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { count } = useCart();
  const { t } = useLang();
  const { features } = useSiteConfig();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    api("/conversations", { auth: true })
      .then((list) => setUnread(list.reduce((a, c) => a + (c.unread || 0), 0)))
      .catch(() => {});
  }, [user, pathname]);

  const tabs = [
    { to: "/", icon: HomeIcon, label: t("tab_home"), tid: "tab-home", exact: true },
    { to: "/categories", icon: LayoutGrid, label: t("tab_categories"), tid: "tab-categories" },
    ...(features.live !== false ? [{ to: "/live", icon: Radio, label: "Live", tid: "tab-live" }] : []),
    { to: "/messages", icon: MessageCircle, label: t("tab_messages"), tid: "tab-messages", badge: unread },
    ...(features.cart !== false ? [{ to: "/cart", icon: ShoppingCart, label: t("tab_cart"), tid: "tab-cart", badge: count }] : []),
    { to: "/account", icon: User, label: t("tab_account"), tid: "tab-account" },
  ];

  const isActive = (tab) => (tab.exact ? pathname === tab.to : pathname.startsWith(tab.to));

  return (
    <nav data-testid="bottom-nav" className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-slate-200 bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((tab) => {
          const active = isActive(tab);
          return (
            <Link
              key={tab.tid}
              data-testid={tab.tid}
              to={tab.to}
              className={`relative flex flex-col items-center gap-0.5 py-2 transition-colors ${
                active ? "text-teal-700" : "text-slate-500 hover:text-teal-600"
              }`}
            >
              <span className="relative">
                <tab.icon className={`h-5 w-5 ${active ? "stroke-[2.4]" : ""}`} />
                {tab.badge > 0 && (
                  <span
                    data-testid={`${tab.tid}-badge`}
                    className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white"
                  >
                    {tab.badge > 99 ? "99+" : tab.badge}
                  </span>
                )}
              </span>
              <span className={`text-[10px] leading-tight ${active ? "font-bold" : "font-medium"}`}>{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
